import type C1_EloItem from "../../ConceptOne/src/model/C1_EloItem";
import type { C1_Consumer } from "../../ConceptOne/src/model/C1_Consumer";
import type { C1_Opening } from "../../ConceptOne/src/model/C1_Opening";
import type { C1_Floor } from "../../ConceptOne/src/model/C1_Floor";
import type { C1_Furniture } from "../../ConceptOne/src/model/C1_Furniture";
import { C1_EloItem_Type } from "../../ConceptOne/src/types/C1_EloTypes";
import { C1_ELO_PLUG_TYPE_LABELS, isGeneratedPlugItemName } from "../../ConceptOne/src/C1_uiLabels";
import { C1_DOOR_TYPE_UI_LABELS, C1_GARAGE_DOOR_TYPE_UI_LABELS } from "../../ConceptOne/src/C1_uiLabels";
import { furnitureResolvers } from "../../ConceptOne/src/furniture/C1_furnitureResolvers";
import { Guid } from "guid-typescript";
import { compareProperties, copyProperties, loadProperties } from "./utils";
import Logger from "../Logger";
import type BaseModel from "./BaseModel";
import Channel from "./Channel";
import { channelTypeInfo } from "./ChannelType";
import { isDevelopmentEnvironment } from "../estada-config";
import { itx, noItx } from "../translationItx/itx";
import { type State } from "../State";
import type { C1_BaseObject } from "../../ConceptOne/src/model/C1_BaseObject";

export enum DisplayNameUsage {
  Default = "default",
  ButtonEditor = "buttonEditor",
  OwnName = "ownName",
  EditorTitle = "editorTitle",
}

/** Naming purpose and optional room already implied by the caller's UI. */
export interface DisplayNameContext {
  usage?: DisplayNameUsage;
  relativeRoom?: BaseObject;
}

export interface DisplayNameParts {
  subject?: string;
  position?: string;
  instance?: string;
  function?: string;
  fallback: string;
}

const logger = new Logger();
const BaseModifiedTime = new Date("1.1.2024").getTime(); // no itx

export enum enumCompareStatus {
  equal = 0,
  newInOtherProject = 2,
  deletedInOtherProject = 4,
  newInCurrentProject = 8,
  deletedInCurrentProject = 16,
  modified = 32,
  childModified = 64,
}

const counter = 0;
export class Event {
  watchers = new Map<
    string,
    (changedProperty: string | any | undefined, changedObject: BaseObject | undefined, source?: string) => void
  >();

  subscribe = (callback: (changedObject: BaseObject, changedProperty: string | any | undefined) => void): string => {
    index++;
    const sIndex = index.toString();
    this.watchers.set(sIndex, callback);
    return sIndex;
  };

  unsubscribe = (id: string) => {
    this.watchers.delete(id);
  };

  raise = (changedProperty: string | object | undefined, changedObject: BaseObject | undefined, source?: string) => {
    this.watchers.forEach((value) => {
      try {
        value(changedProperty, changedObject, source);
      } catch (ex) {
        logger.error(ex);
      }
    });
  };
}

export const globalOnChange = new Event();

export type BeforeChangeCallback = (object: BaseObject, property?: string) => void;
export type ChangedCallback = (object: BaseObject, property?: string) => void;
export type BeforeDeleteCallback = (object: BaseObject) => void;
export type AfterCreateNewCallback = (object: BaseObject) => void;

export function newId(): string {
  const id = Guid.create().toString(); //  + "/" + counter++;
  return id;
}

var index = 0;
export default class BaseObject {
  static logger = new Logger();
  private static beforeChangeCallback?: BeforeChangeCallback;
  private static changedCallback?: ChangedCallback;
  private static beforeDeleteCallback?: BeforeDeleteCallback;
  private static afterCreateNewCallback?: AfterCreateNewCallback;

  static setBeforeChangeCallback(callback?: BeforeChangeCallback) {
    BaseObject.beforeChangeCallback = callback;
  }

  static setChangedCallback(callback?: ChangedCallback) {
    BaseObject.changedCallback = callback;
  }

  static setBeforeDeleteCallback(callback?: BeforeDeleteCallback) {
    BaseObject.beforeDeleteCallback = callback;
  }

  static setAfterCreateNewCallback(callback?: AfterCreateNewCallback) {
    BaseObject.afterCreateNewCallback = callback;
  }

  // -----------------------------------------------------------------------------
  // Optional ConceptOne facet
  // -----------------------------------------------------------------------------
  private _c1?: C1_BaseObject<any>;

  get c1(): C1_BaseObject<any> | undefined {
    return this._c1;
  }

  set c1(newValue: C1_BaseObject<any> | undefined) {
    if (this._c1 === newValue) return;
    this.beforeChange("c1");
    newValue?.attachOwner(this);
    this._c1 = newValue;
    this.changed("c1");
  }

  beforeChange(property?: string) {
    if (globalThis.state?.suppressUndoObservation) return;
    BaseObject.beforeChangeCallback?.(this, property);
  }

  changed(property?: string) {
    if (globalThis.state?.suppressUndoObservation) return;
    this.changedForUndo(property);
    this.raiseOnChange(property);
  }

  protected changedForUndo(property?: string) {
    if (globalThis.state?.suppressUndoObservation) return;
    BaseObject.changedCallback?.(this, property);
  }

  get isBaseModel(): boolean {
    return false;
  }

  // -----------------------------------------------------------------------------
  // sourceId
  // -----------------------------------------------------------------------------
  _sourceId: string | undefined; // only used for copy
  set sourceId(newValue: string | undefined) {
    this._sourceId = newValue;
  }
  get sourceId(): string | undefined {
    return this._sourceId;
  }

  // -----------------------------------------------------------------------------

  _id = newId();

  _propertyId: string | undefined = undefined;

  isSaved: boolean | undefined;

  // Undo monitoring starts only after the object has been attached to the
  // persistent model tree. Constructor assignments are therefore never
  // recorded as property changes.
  monitorForUndo = false;

  setMonitorForUndo(value: boolean, visited = new Set<BaseObject>()) {
    if (visited.has(this)) return;
    visited.add(this);
    this.monitorForUndo = value;
  }

  // -----------------------------------------------------------------------------
  // isLoadedFromPersistence
  // -----------------------------------------------------------------------------
  private _isLoadedFromPersistence: boolean | undefined;
  public get isLoadedFromPersistence(): boolean | undefined {
    return this._isLoadedFromPersistence;
  }
  public set isLoadedFromPersistence(value: boolean | undefined) {
    this._isLoadedFromPersistence = value;
  }

  // timeStamp of last modification of this object

  // -----------------------------------------------------------------------------
  // modifiedTs
  // -----------------------------------------------------------------------------
  _modifiedTs: number = 0;
  get modifiedTs(): number {
    return this._modifiedTs;
  }

  set modifiedTs(newValue: number) {
    if (this._modifiedTs === newValue) return;
    // Technical persistence metadata, not a user-editable property.
    // Do not call beforeChange()/changed(): timestamp updates must not create undo steps.
    this._modifiedTs = newValue;
  }

  // -----------------------------------------------------------------------------

  // non persistent, mostly for AddressGeneration
  isNew?: boolean = false;

  _dirtyChilds?: boolean | undefined;
  public get dirtyChilds(): boolean | undefined {
    return this._dirtyChilds;
  }

  checkDirtyChilds() {
    let res = false;

    const props = this.getPersistentProperties();
    for (const fullkey of props) {
      const attr: any = (this as any)[fullkey] as any;
      if (attr?.isBaseModel) {
        const obj = attr as BaseModel;
        if (obj.isDirty || obj.dirtyChilds) {
          res = true;
          break;
        }
      }
    }

    if (res) {
      this._dirtyChilds = true;
    } else {
      delete this._dirtyChilds;
    }
  }

  public set dirtyChilds(value: boolean | undefined) {
    //
    // set dirtyChilds-staus of all parents
    //
    if (state.showModifiedStatus) {
      this._dirtyChilds = value;
      this.raiseOnChange("dirtyChilds", undefined, true);

      if (this.parent) {
        if (value) {
          this.parent.dirtyChilds = true;
        } else {
          this.parent.checkDirtyChilds();
        }
      }
    } else {
      if (value) {
        this._dirtyChilds = true;
      } else {
        delete this._dirtyChilds;
      }
    }
  }

  clearDirtyChilds() {
    this._dirtyChilds = false;
  }

  //
  //
  // use of this function is only valid during development !!!!!!!
  //
  clearModifiedStatus() {
    const doRaiseOnChange =
      this.isDirty ||
      !this.isLoadedFromPersistence ||
      this._dirtyChilds ||
      this.isNew ||
      this.modifyPropertyHistory ||
      (this as any).deletedItems;

    this.isDirty = false;
    this.isLoadedFromPersistence = true;
    delete this._dirtyChilds;
    delete (this as any).isNew;
    delete this.modifyPropertyHistory;

    // only for BaseModelMaps
    const doRaiseOnChangeChildCollection = (this as any).deletedItems?.size > 0;

    delete (this as any).deletedItems;

    if (doRaiseOnChange) {
      this.raiseOnChangeUI();
    }

    if (doRaiseOnChangeChildCollection) {
      this.parent?.raiseOnChange("childCollection");
    }
  }

  // -----------------------------------------------------------------------------
  // isPersistent
  // -----------------------------------------------------------------------------
  _isPersistent?: boolean;

  get isPersistent(): boolean {
    if (this._isPersistent != undefined) {
      return this._isPersistent;
    }

    if (this.parent == undefined) {
      if (this.propertyId) {
        logger.error(`Object with propertyId ${this.propertyId} has no parent`);
        return true;
      }

      return false;
    }

    return true;
  }

  set isPersistent(newValue: boolean) {
    this._isPersistent = newValue;
  }

  // -----------------------------------------------------------------------------

  get propertyId(): string | undefined {
    return this._propertyId;
  }

  set propertyId(newValue) {
    globalThis.state?.objectRegistry.removeObject(this);
    this._propertyId = newValue;
    globalThis.state?.objectRegistry.registerObject(this);
  }

  get id(): string {
    if (this.propertyId) {
      if (this.parent) {
        return this.parent.id + "_" + this.propertyId;
      } else {
        logger.error(`Object with propertyId ${this.propertyId} has no parent`);
        return "xxxxx" + "_" + this.propertyId;
      }
    } else {
      return this._id;
    }
  }

  set id(newValue) {
    // Object identity is lifecycle metadata, not a normal editable property.
    // Do not call beforeChange()/changed(): assigning an ID must not create an undo step.
    globalThis.state?.objectRegistry.removeObject(this);
    this._id = newValue;
    globalThis.state?.objectRegistry.registerObject(this);
  }

  isDeleting?: boolean; // non persistent:  is set during deleteProcess to enable for Example Registering in deleted Collection of BaseModelMap

  isDeleted = false;

  // -----------------------------------------------------------------------------
  // parent
  // -----------------------------------------------------------------------------
  _parent: BaseObject | undefined;

  get parent(): BaseObject | undefined {
    return this._parent;
  }
  set parent(newValue: BaseObject | undefined) {
    this._parent = newValue;
  }

  // -----------------------------------------------------------------------------
  // state
  // -----------------------------------------------------------------------------
  _state: State | undefined;
  get state(): State {
    return this._state || state;
  }
  ////////////////////////////////////////////////////////////

  // -----------------------------------------------------------------------------
  // errorText
  // -----------------------------------------------------------------------------
  _errorText?: string;
  get errorText(): string | undefined {
    return this._errorText;
  }

  set errorText(newValue: string | undefined) {
    this._errorText = newValue;
  }

  // -----------------------------------------------------------------------------

  //////////////////////////////////////////////////

  _name: string | undefined;
  getName(): string | undefined {
    return undefined;
  }

  setName(newValue: string) {
    this._name = newValue;
  }

  get name(): string {
    return this.getName() || this._name || "";
  }

  set name(newValue: string) {
    this.beforeChange("name");
    this.setName(newValue);

    this.changed("name");
  }

  /** Explicit local names take precedence over computed names in the UI. */
  get displayName(): string {
    return this.getDisplayName();
  }

  set displayName(newValue: string) {
    this.name = newValue;
  }

  getDisplayName(context: DisplayNameContext = {}): string {
    if (context.usage === DisplayNameUsage.EditorTitle && eloItem(this)) {
      const item = eloItem(this)!;
      return [hierarchy(this).owner?.getDisplayName(), item.definition?.description[language()] || item.definitionId]
        .filter(Boolean)
        .join(" / ");
    }
    if (context.usage === DisplayNameUsage.OwnName) return ownName(this);
    if (context.usage === DisplayNameUsage.ButtonEditor) {
      const parts = this.getDisplayNameParts(context);
      return join(parts.function, parts.subject, parts.position) || parts.fallback;
    }
    if ((plugItemName(this) || eloItem(this)?.definition?.defaultName === "") && !this.hasCustomDisplayName()) return defaultName(this);
    return this._name || defaultName(this);
  }

  /** Semantic parts let callers disambiguate labels without parsing formatted text. */
  getDisplayNameParts(context: DisplayNameContext = {}): DisplayNameParts {
    const customName = this.hasCustomDisplayName() ? this._name?.trim() : undefined;
    const fallback = customName || defaultName(this);
    if (customName) return { subject: customName, fallback };
    const item = eloItem(this);
    if (!item) return { subject: fallback, fallback };
    const { owner, ancestors } = hierarchy(this);
    const positioned = ancestors
      .map((ancestor) => eloItem(ancestor)?.definition)
      .find((definition) => definition?.displayPosition?.[language()]?.trim());
    const suffix = this.tag.trim().match(/^[^\d]*\d+(.*)$/)?.[1];
    const prefix = positioned?.tagSuffix;
    const consumer = item.className === "C1_Consumer" ? (item as C1_Consumer) : undefined;
    return {
      subject:
        owner === context.relativeRoom
          ? undefined
          : owner?.c1?.className === "C1_Room"
            ? owner.name.trim() || undefined
            : owner?.getDisplayName(),
      position: positioned?.displayPosition?.[language()]?.trim(),
      instance: prefix && suffix?.startsWith(prefix) ? suffix.slice(prefix.length) || undefined : suffix,
      function:
        consumer?.lightProductDefinition?.descriptionShort[language()] ||
        item.definition?.descriptionShort?.[language()]?.trim(),
      fallback,
    };
  }

  /** Placement changes may update generated labels, but must retain user names. */
  hasCustomDisplayName(): boolean {
    const name = this._name?.trim();
    if (!name) return false;
    const item = eloItem(this),
      definition = item?.definition;
    if (!item || !definition) return true;
    const generated = new Set(definition.generatedNameAliases ?? []);
    if (definition.type === C1_EloItem_Type.Plug && item.data.repeatIndex !== undefined &&
      isGeneratedPlugItemName(name, item.data.repeatIndex)) return false;
    for (const lang of ["de", "en"] as const) {
      const label = definition.description[lang];
      generated.add(label);
      if (item.data.repeatIndex === undefined) continue;
      generated.add(`${label} #${item.data.repeatIndex}`);
    }
    return !generated.has(name);
  }

  //////////////////////////////////////////////////

  getTag(): string | undefined {
    return undefined;
  }

  _tag: string | undefined;
  get tag(): string {
    const t = this.getTag();
    if (t != undefined) {
      return String(t);
    }
    return String(this._tag ?? "");
  }

  setTag(newValue: string) {
    this._tag = newValue;
  }
  set tag(newValue: string) {
    this.beforeChange("tag");
    this.setTag(newValue);

    this.changed("tag");
  }

  get tagWithPrefix(): string {
    return this.tagPrefix + this.getTag();
  }

  get tagOrName(): string {
    return this._tag || this._name || "";
  }
  set tagOrName(newValue: string) {
    this._tag = newValue;
  }

  get nameOrTag(): string {
    return this._name || this._tag || "";
  }

  get extendedTag(): string {
    return this.tag;
  }

  emptyTagIsForbidden(): boolean {
    return true;
  }

  getParentChildrenGroup(): Array<BaseModel> | undefined {
    return undefined;
  }

  ////////////////////////////////////////////////////////////////////////////

  getTagPrefix(): string {
    return state.settings.getTagPrefixByClass(this.className);
  }

  get tagPrefix(): string {
    return this.getTagPrefix();
  }

  get simpleFullTag(): string {
    const thisParts = this.getFullTagParts();
    let res = "." + this.tag;

    let p = this.parent as BaseModel;
    while (p && p.className != "Root" && p.className != "BaseModelMap") {
      if (p.tag) {
        res = "." + p.tag + res;
      }
      p = p.parent as BaseModel;
    }

    if (res.startsWith(".")) {
      res = res.substring(1);
    }
    if (res.endsWith(".")) {
      res = res.substring(0, res.length - 1);
    }

    return res;
  }
  get fullTagParent(): string {
    const s = this.fullTag;
    const ss = s.split("(");
    if (ss.length > 1) {
      return ss[1].substring(0, ss[1].length - 1);
    }
    return "";
  }

  get fullTag(): string {
    //
    // für Supplys geben wir nur zb "230V" aus und ignorieren den channel zb ".a"
    //
    if (this.className == "Channel") {
      const channel: Channel = this as any as Channel;
      if (channel.parent && channelTypeInfo(channel.channelType).isSupply) {
        return channel.parent.fullTag;
      }
    }

    const resCustom = state.nameAndTagManager.getFullTag(this);
    if (resCustom) {
      return resCustom;
    }

    const thisParts = this.getFullTagParts();
    let res = "";
    const skipLeadingDots = true;

    thisParts.forEach((part) => {
      // Imported tags and custom tag parts can contain numbers instead of strings.
      const tag = String(part.tag ?? "").trim();
      const withThisTag = tag !== "";

      // // don`t show cabinetTag if it`s only one cabinet in project
      // if (state.cabinets.size == 1) {
      //   state.cabinets.forEach((cabinet) => {
      //     if (
      //       part.tag == cabinet.tag &&
      //       part.prefix == cabinet.getTagPrefix()
      //     ) {
      //       withThisTag = false;
      //     }
      //   });
      // }

      if (withThisTag) {
        res = res + part.prefix + tag;
      }
    });

    if (skipLeadingDots && res.startsWith(".")) {
      res = res.substring(1);
    }

    if (res.endsWith(".")) {
      res = res.substring(0, res.length - 1);
    }
    return res;
  }

  baseObjectGetFullTagParts(): Array<any> {
    const res = new Array<any>();

    let p: BaseModel | undefined = this as any as BaseModel;
    while (p && p.className != "BaseModelMap" && p.className != "Root") {
      if (p.tag) {
        // skip level with empty tags !!
        res.push({ prefix: p.getTagPrefix(), tag: p.tag || "", obj: p });
      } else {
        //
        // Skip empty tag
        //
        // MWE 12.3.2025
        //
        // Wenn man nur eine Etage hat,
        // und dieser einen leeren Tag gegeben hat
        // dann wird die Etage im if oben übersprungen.
        //
        // dann soll aber der DIN-Präfix "=" trotzen vor dem Raum erscheinen anstelle eines "."
        // Der Punkt würde im Übrigen später noch weg gefiltert
        //

        if (res.length > 0 && p.isFloor) {
          if ((res[res.length - 1].prefix = ".")) {
            res[res.length - 1].prefix = p.getTagPrefix();
          }
        }
      }
      p = p.parent as BaseModel;
    }

    return res.reverse();
  }

  getFullTagParts(): Array<any> {
    return this.baseObjectGetFullTagParts();
  }

  getRelativeTag(relativeObject: BaseModel): string {
    return ""; // is overwritten in BaseModel
  }

  get fullname(): string {
    let res = this.name;

    let o: BaseObject | undefined = this.parent;
    while (o && o.className != "Root" && o.className != "BaseModelMap") {
      const n = o.name || o.tag;
      res = n + "/" + res;
      o = o.parent;
    }

    //
    // trim leading and trailing "/"s
    //
    let indexLast = 0;
    let modified = false;
    do {
      modified = false;
      indexLast = res.lastIndexOf("/");
      if (indexLast >= 0 && indexLast == res.length - 1) {
        res = res.substring(0, indexLast);
        modified = true;
      }
    } while (modified);

    do {
      if (res.startsWith("/")) {
        res = res.substring(1);
      }
    } while (res.startsWith("/"));

    return res;
  }

  //////////////////////////////////////////////////

  public get className(): string {
    logger.error(`property className not implemented for ${this.constructor.name}`);

    return "???";
  }

  public developmentCheckClassName() {
    if (process.env.NODE_ENV === "development") {
      if (this.constructor.name != this.className) {
        logger.error("invalid classname: " + this.className + " should be: " + this.constructor.name);
      }
    }
  }

  public set className(newValue: string) {
    // className can not be set.
    // Just to keep formJson happy
  }

  public get key(): string {
    return this.id;
  }

  onChange = new Event();

  constructor(data?: any, parent?: BaseObject) {
    this.developmentCheckClassName();

    if (this.constructor === BaseObject) {
      throw new Error('Class "BaseObject" cannot be instantiated'); // no itx
    }

    if (parent) {
      this.parent = parent;
    }

    /// results in errors, because not all properties of subclasses are yet initialized
    //  --> copyAllProperties(data, this);

    copyProperties(data, this, "tag", "_name", "parent", "storeInObjectRegistry", "isTemporary", "isPersistent"); // no itx

    this.addToObjectRegistry();
    BaseObject.afterCreateNewCallback?.(this);
  }

  ////////////////////////////////////////////////////////////////////////////////////////

  // -----------------------------------------------------------------------------
  // storeInObjectRegistry
  // -----------------------------------------------------------------------------
  _storeInObjectRegistry = true;

  get storeInObjectRegistry() {
    return this._storeInObjectRegistry;
  }
  set storeInObjectRegistry(newValue) {
    this._storeInObjectRegistry = newValue;
    if (newValue) {
      this.addToObjectRegistry();
    } else {
      this.removeFromObjectRegistry();
    }
  }

  // -----------------------------------------------------------------------------

  addToObjectRegistry() {
    if (this.storeInObjectRegistry) {
      globalThis.state?.objectRegistry.registerObject(this);
    }
  }

  removeFromObjectRegistry() {
    globalThis.state?.objectRegistry.removeObject(this);
  }

  ////////////////////////////////////////////////////////////////////////////////////////////

  delete(): Array<BaseObject> {
    if (!this.isDeleted) {
      BaseObject.beforeDeleteCallback?.(this);
      this.isDeleted = true;
      this.raiseOnChange("@delete");
      this.removeFromObjectRegistry();
      if (this.isLoadedFromPersistence) {
        state.objectRegistry.addToDeletedObjects(this);
      }
      this.initRelations();
      delete this.isDeleting;

      return [this];
    } else return [];
  }

  /** Extension point for model restoration; undo restores lifecycle state through setDeletedForUndo(). */
  undelete() {}

  setDeletedForUndo(deleted: boolean) {
    if (this.isDeleted === deleted) return;
    if (deleted) {
      this.isDeleted = true;
      this.removeFromObjectRegistry();
      if (this.isLoadedFromPersistence) {
        state.objectRegistry.addToDeletedObjects(this);
      }
      this.raiseOnChange("@delete");
    } else {
      this.isDeleted = false;
      globalThis.state?.objectRegistry.registerObject(this);
      state.objectRegistry.removeFromDeletedObjects(this);
      this.raiseOnChange("@undelete");
    }
  }

  compareTo(other: any): string[] {
    return compareProperties(this, other, ...this.getPersistentProperties());
  }

  getPersistentProperties(): string[] {
    // throw new Error(
    //   'Method "getPersistentProperties()" must be implemented.' +
    //     this.className,
    // );
    return ["id", "_name", "tag", "className", "modifiedTs"]; // no itx
  }

  getCompareProperties(): string[] {
    return ["name", "tag"]; // no itx
  }

  toJSON(): any {
    const data: any = {};
    const props = this.getPersistentProperties();
    props.forEach((key) => {
      const attr: any = (this as any)[key] as any;
      if (attr != undefined) {
        if (attr.toJSON) {
          data[key] = attr.toJSON();
        } else {
          data[key] = attr;
        }
      }
    });
    return data;
    // throw new Error('Method "toJSON()" must be implemented.');
  }

  fromJSON(data: any, parent: BaseObject | undefined = undefined): void {
    // throw new Error('Method "fromJSON()" must be implemented.');
    this.parent = parent;
    loadProperties(data, this);
    this.storeInObjectRegistry = true;
  }

  createUndoSnapshot(): any {
    return this.toJSON();
  }

  restoreUndoSnapshot(data: any) {
    this.fromJSON(data, this.parent);
  }

  compareStatus: enumCompareStatus = enumCompareStatus.equal;

  // -----------------------------------------------------------------------------
  // isDirty
  // -----------------------------------------------------------------------------
  _isDirty = false;
  get isDirty(): boolean {
    return this._isDirty;
  }
  set isDirty(newValue: boolean) {
    this._isDirty = newValue;
    if (newValue) {
      state.autobackupNeeded = true;
    }
  }

  // -----------------------------------------------------------------------------

  static runWithoutMarkAsDirty(f: () => void) {
    BaseObject.suppresMarkAsDirty = true;
    try {
      f();
    } catch (ex) {
      logger.error(ex);
    }
    BaseObject.suppresMarkAsDirty = false;
  }

  static suppresMarkAsDirty = false;

  oldValues?: any;
  modifyPropertyHistory?: Array<{ name: string; newValue: string }>;

  pushModifyPropertyHistory(name: string, newValue: string) {
    if (isDevelopmentEnvironment()) {
      if (!this.modifyPropertyHistory) {
        this.modifyPropertyHistory = [];
      }
      this.modifyPropertyHistory.push({ name: name, newValue: newValue });
    }
  }

  markAsDirty(property?: string) {
    if (!this.isPersistent) {
      return;
    }

    if (BaseObject.suppresMarkAsDirty) {
      return;
    }

    if (
      this.className != "SelectionManager" &&
      this.className != "DialogManager" &&
      this.className != "ContextMenuManager"
    ) {
      if (state.showModifiedStatus) {
        this.pushModifyPropertyHistory(property || noItx("--?"), property ? (this as any)[property] : "");
      }

      if (!this.isDirty) {
        logger.log(`markAsDirty: ${this.className} property=${property}`);
      }

      if (property == "errors") {
        return;
      }

      if (property == "warnings") {
        return;
      }

      if (!this.isDirty) {
        this.isDirty = true;

        if (this.parent) {
          //
          // set dirtyChilds-staus of all parents (recusion upwards inside setter)
          //
          this.parent.dirtyChilds = true;
        }
      }
    }
    this.modifiedTs = new Date().getTime() - BaseModifiedTime;
  }

  clearDirty() {
    this.isDirty = false;
    delete this.dirtyChilds;
  }

  raiseOnChangeUI(changedProperty?: string) {
    this.raiseOnChange(changedProperty, undefined, true);
  }

  raiseOnChange(changedProperty: string | any | undefined = undefined, eventSource?: string, uiOnly?: boolean) {
    if (!uiOnly && (globalThis.state?.isLoadingFromEstadaStorage || globalThis.state?.suppressUndoObservation)) {
      return;
    }

    if (!uiOnly) {
      if (changedProperty) {
        const props = this.getPersistentProperties();
        if (props.includes(changedProperty)) {
          this.markAsDirty(changedProperty);
        }
      } else {
        this.markAsDirty(undefined);
      }
    }

    if (!this.parent && this.className == "BaseModelMap") {
      logger.error(
        `BaseModelMap.parent is undefined in rasieOnChange ${this.className}. Is this a temporary object ???`,
      );
    } else {
      // BaseObject.logger.log(` raiseOnChange ${this.name} ${changedProperty}`);
      this.onChange.raise(this, changedProperty, eventSource);
      globalOnChange.raise(this, changedProperty, eventSource);
    }
  }

  raiseOnUpdateUi(changedProperty: string | any | undefined = undefined) {
    this.raiseOnChange(changedProperty, undefined, true);
  }

  get sourceObject(): BaseObject | undefined {
    return state.objectRegistry.getObject(this.sourceId);
  }

  //////////////////////////////////////////////////////
  ///////////// for paste
  get mapAfterCopyDone(): boolean {
    return this.sourceId ? true : false;
  }
  mapAfterCopy() {
    const id1 = state.objectMapping.getMappedId(this.id);
    if (id1) {
      if (this.mapAfterCopyDone) {
        // mapAfterCopy called twice.
        //    This is not an error
      } else {
        //
        // Wir setzten hier "_id" anstatt "id"
        // Der setter entfernt die alte id aus der Registry
        //
        // das führt dazu, dass der "alte id vor Kopieren" in der Object Registry erhalten bleibt
        // das Object ist somit 2x registriert: unter seiner alte d und seiner neuen nach Kopieren
        //
        this.sourceId = this._id;
        this._id = id1;
      }
    }
  }

  async pasteItem(params: { item: BaseModel; allItems: Array<BaseModel> }): Promise<Array<BaseModel>> {
    const { item } = params;
    logger.error("pasteItem not implementet for class");
    return [];
  }

  initRelations() {}

  toString(): string {
    return this.tag + " " + this.name;
  }
}

// Shared naming rules read facet metadata; facets never compute or forward display names.
function eloItem(object: BaseObject | undefined): C1_EloItem | undefined {
  const facet = object?.c1;
  return facet && "_defId" in facet ? (facet as C1_EloItem) : undefined;
}

function language() {
  return globalThis.state?.c1?.language ?? "de";
}

function join(...parts: (string | number | undefined)[]) {
  return parts.filter((part) => part !== undefined && part !== "").join(" ");
}

function compose(name: string, owner?: string, suffix?: string) {
  return join(name, owner, suffix ? `(${suffix})` : undefined);
}

function template(text: string | undefined, values: Record<string, unknown>): string | undefined {
  if (!text) return undefined;
  let complete = true;
  const result = text.replace(/\$\{\s*([^}]+?)\s*\}/g, (_match, key: string) => {
    const value = values[key.trim()];
    if (value === undefined || value === null) complete = false;
    return String(value ?? "");
  });
  return complete ? result : undefined;
}

function hierarchy(object: BaseObject) {
  const ancestors: BaseObject[] = [];
  let owner = object.parent;
  while (owner && eloItem(owner)) {
    ancestors.push(owner);
    owner = owner.parent;
  }
  const room = owner?.c1?.className === "C1_Room" ? owner : owner?.parent;
  const opening = owner?.c1 as C1_Opening | undefined;
  return {
    owner,
    ancestors,
    values: {
      doorRoomName: room?.name,
      partnerRoomName: opening?.partnerRoomName,
      objectName: owner?.name,
      furnitureDescription: owner?.c1?.className === "C1_Furniture" ? owner.getDisplayName() : undefined,
    },
  };
}

function ownName(object: BaseObject): string {
  const item = eloItem(object),
    definition = item?.definition;
  if (!item) return object._name || defaultName(object);
  if (plugItemName(object) && !object.hasCustomDisplayName()) return "";
  const paletteId = item.data.interactivePlacement?.paletteId;
  const generatedLight =
    item.interactivCreated &&
    (paletteId === "electricalLight" || paletteId === "outdoorLight") &&
    !object.hasCustomDisplayName();
  if (definition?.defaultName === "" && !object.hasCustomDisplayName()) return "";
  if (object._name?.trim() && !generatedLight) return object._name.trim();
  return (
    template(definition?.descriptionTemplate?.[language()], hierarchy(object).values) ||
    definition?.description[language()] ||
    definition?.definitionId ||
    ""
  );
}

/** Socket slots derive their visible label from the owner, never from a saved detail snapshot. */
function plugItemName(object: BaseObject): string | undefined {
  const item = eloItem(object);
  if (item?.className !== "C1_Consumer" || item.definition?.type !== C1_EloItem_Type.Plug ||
    item.data.repeatIndex === undefined) return undefined;
  const label = C1_ELO_PLUG_TYPE_LABELS[(item as C1_Consumer).channelType];
  return label ? `#${item.data.repeatIndex} - ${label}` : undefined;
}

function defaultName(object: BaseObject): string {
  const plugName = plugItemName(object);
  if (plugName) return plugName;
  //
  // direkte Kinder von Gruppen, erben den Namen der Gruppe
  // wenn der eigene Name nicht gesetzt ist.
  //
  if (object.parent?.className === "Group") {
    if (eloItem(object)?.definition?.defaultName === "") return object.parent.getDisplayName();
    const name = object.parent.name?.trim();
    if (name) return name;
  }

  const facet = object.c1;
  if (object.className === "C1_Project") return state.projectMetadata.name.trim() || "Concept One";
  switch (facet?.className) {
    case "C1_Furniture": {
      const furniture = facet as C1_Furniture;
      return furnitureResolvers.displayName?.(furniture.definitionId) || `?? ${furniture.definitionId}`;
    }
    case "C1_Opening": {
      const opening = facet as C1_Opening;
      if (opening.kind === "window")
        return join(itx("c1.ui.Fenster", "Fenster"), opening.masterIndex, (opening.room?.owner?.name ?? "").trim());
      if (opening.kind === "garage-door") return C1_GARAGE_DOOR_TYPE_UI_LABELS[opening.garageDoorType];
      if (opening.doorType === "passage") return join(C1_DOOR_TYPE_UI_LABELS.passage, opening.partnerRoomName);
      return opening.doorType === "interior" && opening.partnerRoomName
        ? join(itx("c1.ui.Tur", "Tür"), opening.partnerRoomName)
        : C1_DOOR_TYPE_UI_LABELS[opening.doorType];
    }
    case "C1_Room":
      return object.name.trim() || itx("c1.ui.unnamed", "Unbenannt");
    case "C1_Floor":
      return object.name.trim() || `Geschoss ${(facet as C1_Floor).level}`;
  }
  const item = eloItem(object);
  if (!item) return object.isBaseModel ? (object as BaseModel).nameOfDefaultName : object.name;
  const { owner, ancestors, values } = hierarchy(object);
  const definition = item.definition;
  const itemName = definition?.description[language()] || ownName(object);
  if (!owner) return itemName;
  const resolved = template(definition?.descriptionTemplate?.[language()], values);
  const ancestorContext = ancestors
    .map((ancestor) => {
      const def = eloItem(ancestor)?.definition;
      return { name: template(def?.descriptionTemplate?.[language()], values), suffix: def?.tagSuffix };
    })
    .find((context) => context.name);
  const tagSuffix = object.tag.trim().match(/^[^\d]*\d+(.*)$/)?.[1];
  const suffix =
    ancestorContext?.suffix && tagSuffix?.startsWith(ancestorContext.suffix)
      ? tagSuffix.slice(ancestorContext.suffix.length)
      : tagSuffix;
  const hideType =
    !!definition?.type && definition.type !== C1_EloItem_Type.Group && definition.type !== C1_EloItem_Type.PlugGroup;
  switch (owner.c1?.className) {
    case "C1_Room":
      return (
        resolved || (hideType ? compose(owner.name.trim(), undefined, suffix) : compose(itemName, owner.name.trim()))
      );
    case "C1_Opening": {
      const opening = owner.c1 as C1_Opening;
      if (opening.kind === "window") {
        if (resolved) return compose(resolved, undefined, suffix);
        const name = join(language() === "de" ? "Fenster" : "Window", opening.masterIndex);
        return hideType ? compose(name, undefined, suffix) : compose(itemName, name);
      }
      return resolved || (hideType ? owner.getDisplayName() : compose(itemName, owner.getDisplayName()));
    }
    case "C1_Furniture": {
      const name = ancestorContext?.name || owner.getDisplayName();
      return resolved
        ? compose(resolved, undefined, suffix)
        : hideType
          ? compose(name, undefined, suffix)
          : compose(itemName, name, suffix);
    }
    default:
      return itemName;
  }
}
